// ══════════════════════════════════════════════════════
// Trade Insights (Phase 2d)
// Rule-based Interpretation der Trade-Level-Stats.
// Kein LLM, keine externen Deps — deterministisch und schnell.
//
// 5 Kategorien:
//   1. Exit-Qualität  (aus exit_reasons)
//   2. Entry-Qualität (aus mae_mfe_pairs + summary)
//   3. Timing         (aus heatmap)
//   4. Equity         (aus equity_curve + trades)
//   5. Distribution   (aus summary + trades)
//
// Design-Prinzip: Jede Regel hat Mindest-Datenvolumen. Bei zu wenig Trades
// wird die Kategorie übersprungen, nicht mit unfundierten Aussagen gefüllt.
// ══════════════════════════════════════════════════════

import { theme, fonts } from "../theme";

// ═════════════════════════════════════════════════════════
// HELPERS
// ═════════════════════════════════════════════════════════
const dollar = (v, d = 0) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 10000) return `${n < 0 ? "-" : ""}$${(abs / 1000).toFixed(1)}k`;
  return `${n < 0 ? "-" : ""}$${abs.toFixed(d)}`;
};
const pct = (v, d = 0) => `${Number(v || 0).toFixed(d)}%`;

// Severity → Icon + Farbe (matches BtDetail-Stil)
const SEV = {
  positive: { icon: "✅", color: theme.greenText, bg: theme.greenLight2 },
  info:     { icon: "📊", color: theme.text2,    bg: theme.tint },
  warning:  { icon: "⚠️", color: theme.amberText, bg: theme.amberLight },
  issue:    { icon: "🛡️", color: theme.redText,   bg: theme.redLight2 },
};

// ═════════════════════════════════════════════════════════
// RULE GENERATORS
// Jede Funktion returnt eine Liste von Insights
// { severity, text, category }
// ═════════════════════════════════════════════════════════

// ─── 1. EXIT-QUALITÄT ──────────────────────────────────
function exitRules(stats, total) {
  const out = [];
  if (total < 20) return out;  // zu wenig Daten für Exit-Analyse
  const reasons = stats.exit_reasons || [];
  const byName = Object.fromEntries(reasons.map(r => [r.reason, r]));
  const share = name => (byName[name]?.count || 0) / total * 100;

  const slShare = share("SL");
  const beShare = share("BE");
  const mbShare = share("MaxBars");
  const tp1Share = share("TP1");
  const trailShare = share("Trail");
  const structShare = share("Struct SL");

  if (slShare > 35) {
    out.push({ severity: "issue", category: "Exit",
      text: `${pct(slShare)} SL-Exits (${byName.SL.count} Trades, ${dollar(byName.SL.total_pnl,0)}) — Stops werden häufig gerissen. Prüfe größere SL-Distanz, bessere Entry-Filter oder strengeren Regime-Filter.` });
  } else if (slShare < 18 && total > 50) {
    out.push({ severity: "positive", category: "Exit",
      text: `${pct(slShare)} SL-Exits — Stops werden selten getroffen. Gute Entry-Qualität.` });
  }

  if (beShare > 30) {
    out.push({ severity: "warning", category: "Exit",
      text: `${pct(beShare)} Break-Even-Exits (${byName.BE.count} Trades) — BE-Trigger greift zu früh, Trades bekommen kein Follow-Through. Evaluiere größeres be_trigger_rr.` });
  }

  if (mbShare > 12) {
    out.push({ severity: "warning", category: "Exit",
      text: `${pct(mbShare)} MaxBars-Exits (${byName.MaxBars.count} Trades, ${dollar(byName.MaxBars.total_pnl,0)}) — Strategie hält oft zu lange. Evaluiere max_bars_in_trade oder Zeit-basierten Exit.` });
  }

  const tpGoodShare = trailShare + structShare;
  if (tpGoodShare > 25) {
    out.push({ severity: "positive", category: "Exit",
      text: `${pct(tpGoodShare)} Trail/Struct-SL-Exits — Strategie nutzt Trends gut aus und lässt Winner laufen.` });
  }

  if (tp1Share > 55) {
    out.push({ severity: "info", category: "Exit",
      text: `${pct(tp1Share)} TP1-Exits — Trades schließen häufig am ersten Ziel. Bei Trend-Regimen evaluiere Partial + Trail statt Full-Exit.` });
  }

  return out;
}

// ─── 2. ENTRY-QUALITÄT (MAE/MFE) ───────────────────────
function entryQualityRules(stats, total, trades) {
  const out = [];
  const pairs = stats.mae_mfe_pairs || [];
  if (pairs.length < 15) return out;

  const s = stats.summary || {};
  const avgMae = Math.abs(s.avg_mae || 0);
  const avgMfe = s.avg_mfe || 0;

  if (avgMae > 0.01) {
    const ratio = avgMfe / avgMae;
    if (ratio > 2.0) {
      out.push({ severity: "positive", category: "Entry",
        text: `Ø MFE/|MAE| = ${ratio.toFixed(2)} — sehr gute Entry-Qualität. Trades laufen typisch weit vor Gegenbewegung (MFE ${dollar(avgMfe,0)} vs MAE ${dollar(-avgMae,0)}).` });
    } else if (ratio < 1.2) {
      out.push({ severity: "issue", category: "Entry",
        text: `Ø MFE/|MAE| = ${ratio.toFixed(2)} — reaktive Entries. Trades sehen im Schnitt erst Gegenbewegung (MAE ${dollar(-avgMae,0)}) bevor sie laufen (MFE ${dollar(avgMfe,0)}). Swing-Length / Entry-Mode überdenken.` });
    }
  }

  // Loser mit hohem MFE → "Winner nicht laufen lassen"
  const avgWin = Math.abs(s.avg_win || 0);
  if (avgWin > 0.01) {
    const hotMfeThreshold = avgWin * 1.5;
    const missedWinners = pairs.filter(p => (p.pnl || 0) < 0 && (p.mfe || 0) >= hotMfeThreshold).length;
    if (missedWinners >= 5 && missedWinners / pairs.length >= 0.05) {
      out.push({ severity: "warning", category: "Entry",
        text: `${missedWinners} Loser-Trades (${pct(missedWinners/pairs.length*100)}) hatten MFE ≥ ${dollar(hotMfeThreshold,0)} (1.5× Ø-Win). Potentielle Winner die nicht mitgenommen wurden — Trail-Aktivierung zu spät / kein Partial?` });
    }
  }

  // Trades mit viel MAE, wenig MFE → "Entries sofort im Minus"
  const ugly = pairs.filter(p => {
    const mae = Math.abs(p.mae || 0);
    const mfe = p.mfe || 0;
    return mae > avgMae && mfe < avgMfe * 0.3;
  }).length;
  if (ugly >= 5 && ugly / pairs.length >= 0.08) {
    out.push({ severity: "warning", category: "Entry",
      text: `${ugly} Trades (${pct(ugly/pairs.length*100)}) mit hohem MAE bei niedrigem MFE — sofort im Minus, nie echt gelaufen. Mögliches Entry-Timing-Problem (zu früh / gegen HTF-Struktur).` });
  }

  return out;
}

// ─── 3. TIMING (Heatmap) ───────────────────────────────
function timingRules(stats, total) {
  const out = [];
  if (total < 30) return out;
  const cells = stats.heatmap?.cells || [];
  if (cells.length === 0) return out;
  const weekdayLabels = stats.heatmap?.weekday_labels || ["Mo","Di","Mi","Do","Fr","Sa","So"];

  // Top-3 Stunden-Konzentration (ignoriert Weekday)
  const byHour = {};
  cells.forEach(c => {
    const h = c.hour;
    if (!byHour[h]) byHour[h] = { hour: h, count: 0, pnl: 0 };
    byHour[h].count += c.count || 0;
    byHour[h].pnl += c.pnl || 0;
  });
  const hoursSorted = Object.values(byHour).sort((a,b) => b.count - a.count);
  const top3Share = hoursSorted.slice(0,3).reduce((s,h) => s + h.count, 0) / total * 100;
  if (top3Share > 50) {
    const top3Str = hoursSorted.slice(0,3)
      .map(h => `${String(h.hour).padStart(2,"0")}:00 (${h.count})`).join(", ");
    out.push({ severity: "info", category: "Timing",
      text: `${pct(top3Share)} aller Trades in Top-3 Stunden: ${top3Str}. Strategie ist stark sitzungsabhängig.` });
  }

  // Worst Hour: negativer PnL, count ≥ max(5, total*0.04)
  const minCellCount = Math.max(5, Math.floor(total * 0.04));
  const worstHour = Object.values(byHour)
    .filter(h => h.count >= minCellCount && h.pnl < 0)
    .sort((a,b) => a.pnl - b.pnl)[0];
  if (worstHour && Math.abs(worstHour.pnl) > Math.abs(stats.summary?.total_pnl || 0) * 0.15) {
    out.push({ severity: "issue", category: "Timing",
      text: `${String(worstHour.hour).padStart(2,"0")}:00-Uhr-Trades: ${dollar(worstHour.pnl,0)} über ${worstHour.count} Trades (Ø ${dollar(worstHour.pnl/worstHour.count,0)}/Trade). Bad-Hour-Filter evaluieren.` });
  }

  // Best Hour (positiv, konzentriert)
  const bestHour = Object.values(byHour)
    .filter(h => h.count >= minCellCount && h.pnl > 0)
    .sort((a,b) => (b.pnl/b.count) - (a.pnl/a.count))[0];
  if (bestHour && bestHour.count >= minCellCount) {
    const avgP = bestHour.pnl / bestHour.count;
    if (avgP > 0 && bestHour.pnl > Math.abs(stats.summary?.total_pnl || 1) * 0.1) {
      out.push({ severity: "positive", category: "Timing",
        text: `${String(bestHour.hour).padStart(2,"0")}:00-Uhr ist beste Stunde: ${dollar(bestHour.pnl,0)} über ${bestHour.count} Trades (Ø ${dollar(avgP,0)}/Trade).` });
    }
  }

  // Worst Weekday
  const byWd = {};
  cells.forEach(c => {
    const w = c.weekday;
    if (!byWd[w]) byWd[w] = { weekday: w, count: 0, pnl: 0 };
    byWd[w].count += c.count || 0;
    byWd[w].pnl += c.pnl || 0;
  });
  const worstWd = Object.values(byWd)
    .filter(d => d.count >= minCellCount * 2 && d.pnl < 0)
    .sort((a,b) => a.pnl - b.pnl)[0];
  if (worstWd && Math.abs(worstWd.pnl) > Math.abs(stats.summary?.total_pnl || 0) * 0.2) {
    out.push({ severity: "warning", category: "Timing",
      text: `${weekdayLabels[worstWd.weekday]} ist schwachster Wochentag: ${dollar(worstWd.pnl,0)} über ${worstWd.count} Trades. Weekday-Filter evaluieren.` });
  }

  return out;
}

// ─── 4. EQUITY (Streaks + Plateaus) ─────────────────────
function equityRules(stats, total, trades) {
  const out = [];
  if (total < 20) return out;

  // Chronologische Trades (wie in equity_curve)
  const chrono = (trades || []).filter(t => t.exit_time && t.pnl != null)
    .sort((a,b) => (a.exit_time > b.exit_time ? 1 : -1));
  if (chrono.length < 20) return out;

  // Längste Losing-Streak
  let curLoss = 0, curLossPnl = 0;
  let maxLossCount = 0, maxLossPnl = 0;
  for (const t of chrono) {
    if ((t.pnl || 0) < 0) {
      curLoss++; curLossPnl += t.pnl;
      if (curLoss > maxLossCount) { maxLossCount = curLoss; maxLossPnl = curLossPnl; }
    } else {
      curLoss = 0; curLossPnl = 0;
    }
  }
  if (maxLossCount >= 7) {
    out.push({ severity: "warning", category: "Equity",
      text: `Längste Losing-Streak: ${maxLossCount} Trades (${dollar(maxLossPnl,0)}). Position-Sizing bei Streaks reduzieren oder Strategy-Drawdown-Pause triggern?` });
  } else if (maxLossCount <= 3 && chrono.length > 40) {
    out.push({ severity: "positive", category: "Equity",
      text: `Längste Losing-Streak nur ${maxLossCount} Trades — Verluste bleiben gestreut, gute Stabilität.` });
  }

  // Plateau-Detection: längstes Fenster ohne Net-PnL-Fortschritt
  // Trades werden in Chunks von ~max(20, total*0.05) ausgewertet
  const windowSize = Math.max(20, Math.floor(chrono.length * 0.1));
  if (chrono.length >= windowSize * 3) {
    let cumPnl = 0;
    const cumulative = chrono.map(t => cumPnl += (t.pnl || 0));
    let maxPlateauLen = 0, plateauStart = 0;
    for (let i = 0; i < cumulative.length - windowSize; i++) {
      // Plateau wenn cumulative[j] ≤ cumulative[i] für alle j ∈ [i, j_max]
      const basePnl = cumulative[i];
      let j = i + 1;
      while (j < cumulative.length && cumulative[j] <= basePnl + Math.abs(basePnl) * 0.02) j++;
      const len = j - i;
      if (len > maxPlateauLen) {
        maxPlateauLen = len;
        plateauStart = i;
      }
    }
    if (maxPlateauLen >= windowSize * 2) {
      out.push({ severity: "warning", category: "Equity",
        text: `Plateau von ${maxPlateauLen} Trades ohne Net-PnL-Fortschritt (Trade #${plateauStart+1} bis #${plateauStart+maxPlateauLen}). Mögliche Regime-Abhängigkeit — Regime-Filter evaluieren.` });
    }
  }

  // Max-DD Bewertung
  const s = stats.summary || {};
  if (s.max_dd_trade_pct > 30 && total > 30) {
    out.push({ severity: "issue", category: "Equity",
      text: `Max Trade-Drawdown: ${dollar(s.max_dd_trade_usd,0)} (${pct(s.max_dd_trade_pct)} vom Peak) — hoch. Risk-Parameter (Position-Size, SL) härten.` });
  } else if (s.max_dd_trade_pct > 0 && s.max_dd_trade_pct < 12 && total > 40) {
    out.push({ severity: "positive", category: "Equity",
      text: `Max Trade-Drawdown: ${dollar(s.max_dd_trade_usd,0)} (${pct(s.max_dd_trade_pct)}) — gut kontrolliert.` });
  }

  return out;
}

// ─── 5. DISTRIBUTION (WR/PF/Concentration) ──────────────
function distributionRules(stats, total, trades) {
  const out = [];
  if (total < 20) return out;
  const s = stats.summary || {};

  // Trading-Profil (WR + Expectancy)
  const wr = s.win_rate || 0;
  const exp = s.expectancy || 0;
  const pf = s.profit_factor;

  if (wr < 42 && exp > 0) {
    out.push({ severity: "info", category: "Profil",
      text: `WR ${pct(wr)} + Expectancy ${dollar(exp,2)}/Trade → Trend-Following-Profil. Niedrige WR wird durch große Winner kompensiert (Ø Win ${dollar(s.avg_win,0)} vs Ø Loss ${dollar(s.avg_loss,0)}).` });
  } else if (wr > 58 && exp > 0) {
    out.push({ severity: "info", category: "Profil",
      text: `WR ${pct(wr)} mit Ø Win ${dollar(s.avg_win,0)} / Ø Loss ${dollar(s.avg_loss,0)} → Mean-Revert-Profil. Achte darauf dass der Ø Win groß genug bleibt.` });
  }

  // PF-Bewertung
  if (pf != null) {
    if (pf >= 2.0) {
      out.push({ severity: "positive", category: "Profil",
        text: `PF ${pf.toFixed(2)} — solide profitable Basis. Kombiniert mit Exp ${dollar(exp,2)}/Trade ist das eine tragfähige Kurve.` });
    } else if (pf < 1.1 && total > 40) {
      out.push({ severity: "issue", category: "Profil",
        text: `PF ${pf.toFixed(2)} — marginal. Kleine Parameter-Drift kann die Kurve kippen lassen. Walk-Forward-Robustheit prüfen.` });
    }
  }

  // Concentration: wie viel macht Top-N-Trades aus?
  if (trades && trades.length >= 30) {
    const sorted = [...trades].filter(t => t.pnl != null)
      .sort((a,b) => (b.pnl || 0) - (a.pnl || 0));
    const totalPnl = sorted.reduce((sum, t) => sum + (t.pnl || 0), 0);
    if (Math.abs(totalPnl) > 0.01) {
      const topShare = sorted.slice(0, 5).reduce((sum, t) => sum + (t.pnl || 0), 0) / totalPnl * 100;
      if (topShare > 60 && totalPnl > 0) {
        out.push({ severity: "warning", category: "Profil",
          text: `Top 5 Trades tragen ${pct(topShare)} des Total-PnL (${dollar(totalPnl,0)}). Starke Konzentration — PnL hängt an wenigen Ausreißern. Ohne diese Trades wäre das Ergebnis deutlich schwächer.` });
      } else if (topShare < 30 && totalPnl > 0) {
        out.push({ severity: "positive", category: "Profil",
          text: `Top 5 Trades tragen nur ${pct(topShare)} des Total-PnL — Gewinn ist breit gestreut, robuste Kurve.` });
      }
    }
  }

  // Sample-Size-Warning
  if (total < 30) {
    out.push({ severity: "warning", category: "Sample",
      text: `Nur ${total} Trades — statistische Aussagekraft limitiert. Erkenntnisse mit Vorsicht lesen, längeren Zeitraum oder mehr Daten empfohlen.` });
  }

  return out;
}

// ═════════════════════════════════════════════════════════
// MAIN: TradeInsights
// Props:
//   stats: aus Aggregator
//   trades: Trade-Liste (Phase-1a-Schema)
// ═════════════════════════════════════════════════════════
export default function TradeInsights({ stats, trades }) {
  if (!stats || !stats.summary) return null;
  const total = stats.summary.total_trades || 0;

  if (total === 0) {
    return (
      <div style={{
        padding: "14px 18px", background: theme.tint,
        border: `1px solid ${theme.border}`, borderRadius: 10,
        fontSize: 12, color: theme.text3, fontStyle: "italic",
      }}>
        Keine Trades für Interpretation.
      </div>
    );
  }

  const allInsights = [
    ...exitRules(stats, total),
    ...entryQualityRules(stats, total, trades),
    ...timingRules(stats, total),
    ...equityRules(stats, total, trades),
    ...distributionRules(stats, total, trades),
  ];

  if (allInsights.length === 0) {
    return (
      <div style={{
        padding: "14px 18px", background: theme.tint,
        border: `1px solid ${theme.border}`, borderRadius: 10,
        fontSize: 12, color: theme.text3,
      }}>
        Keine auffälligen Muster bei {total} Trades — Strategie läuft in der Mitte der Verteilungen.
      </div>
    );
  }

  // Sortierung: issue > warning > positive > info
  const sevOrder = { issue: 0, warning: 1, positive: 2, info: 3 };
  allInsights.sort((a, b) => sevOrder[a.severity] - sevOrder[b.severity]);

  // Count-Badges pro Severity für die Sektion-Header
  const counts = allInsights.reduce((m, i) => {
    m[i.severity] = (m[i.severity] || 0) + 1; return m;
  }, {});

  return (
    <div>
      {/* Severity-Count-Zeile */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {counts.issue > 0 && (
          <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12,
                         background: theme.redLight, color: theme.redText,
                         fontWeight: 700 }}>
            {counts.issue} Problem{counts.issue !== 1 ? "e" : ""}
          </span>
        )}
        {counts.warning > 0 && (
          <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12,
                         background: theme.amberLight, color: theme.amberText,
                         fontWeight: 700 }}>
            {counts.warning} Warnung{counts.warning !== 1 ? "en" : ""}
          </span>
        )}
        {counts.positive > 0 && (
          <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12,
                         background: theme.greenLight, color: theme.greenText,
                         fontWeight: 700 }}>
            {counts.positive} positiv
          </span>
        )}
        {counts.info > 0 && (
          <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12,
                         background: theme.blueLight, color: theme.blueText,
                         fontWeight: 700 }}>
            {counts.info} Info
          </span>
        )}
      </div>

      {/* Insight-Grid — kompakt, auf einen Blick erfassbar */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {allInsights.map((ins, i) => {
          const sev = SEV[ins.severity] || SEV.info;
          return (
            <div key={i} style={{
              display: "flex", gap: 10, padding: "8px 12px",
              background: sev.bg, borderRadius: 8,
              border: `1px solid ${sev.color}22`,
              alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.3 }}>
                {sev.icon}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, color: sev.color, fontWeight: 700,
                              textTransform: "uppercase", letterSpacing: "0.3px",
                              fontFamily: fonts.mono, marginBottom: 2, opacity: 0.7 }}>
                  {ins.category}
                </div>
                <div style={{ fontSize: 13, color: sev.color, lineHeight: 1.45, fontWeight: 500 }}>
                  {ins.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
